import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Wifi, WifiOff, Play, Pause, Settings, LogOut, Bell } from 'lucide-react';
import { useMarketData, useConnection, useTrading, useEngine } from '../context/MarketContext';
import { useAppSelector } from '../store/hooks';
import { useAuth } from '../context/AuthContext';
import type { PageKey } from '../types';

type NotifType = 'connection' | 'activity' | 'engine' | 'order';
interface Notif { id: string; type: NotifType; message: string; time: number; read: boolean; }
const makeNotif = (type: NotifType, message: string): Notif => ({
  id: `${type[0]}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  type, message, time: Date.now(), read: false,
});

export interface AppHeaderProps {
  onNavigate: (page: PageKey) => void;
  instruments: { label: string; secId: string }[];
}

export function AppHeader({ onNavigate, instruments }: AppHeaderProps) {
  const { selectedSymbol, setSelectedSymbol, INSTRUMENTS } = useMarketData();
  const { connectionStatus, messages } = useConnection();
  const { isAutoTrading, setIsAutoTrading, orders, portfolio } = useTrading();
  const { activityFeed, engineConfig } = useEngine();
  const { user, logout } = useAuth();
  const vwapMap = useAppSelector(s => s.chartData.vwap);

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef(0);
  const prevConn = useRef(connectionStatus);
  const seenActivityIds = useRef<Set<string>>(new Set());
  const prevOrdersRef = useRef<typeof orders>([]);

  // Close settings dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(e.target as Node)) {
        setIsSettingsDropdownOpen(false);
      }
    };
    if (isSettingsDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isSettingsDropdownOpen]);

  // Track connection status changes
  useEffect(() => {
    if (prevConn.current === connectionStatus) return;
    const msg = connectionStatus === 'connected'
      ? 'FIX connection established'
      : connectionStatus === 'disconnected'
      ? 'FIX connection lost'
      : 'Reconnecting to FIX…';
    setNotifs(prev => [makeNotif('connection', msg), ...prev].slice(0, 40));
    prevConn.current = connectionStatus;
  }, [connectionStatus]);

  // Track bursts of new FIX messages
  useEffect(() => {
    const count = messages.length;
    if (count >= prevMsgCount.current + 20) {
      setNotifs(prev => [makeNotif('activity', `${count - prevMsgCount.current} new FIX messages received`), ...prev].slice(0, 40));
      prevMsgCount.current = count;
    } else {
      prevMsgCount.current = count;
    }
  }, [messages.length]);

  // Wire activityFeed engine events into bell
  useEffect(() => {
    const newEvents = activityFeed.filter(ev => !seenActivityIds.current.has(ev.id));
    if (newEvents.length === 0) return;
    newEvents.forEach(ev => seenActivityIds.current.add(ev.id));
    const mapped = newEvents.map(ev => makeNotif('engine', `${ev.title}${ev.detail ? ' — ' + ev.detail : ''}`));
    setNotifs(prev => [...mapped, ...prev].slice(0, 60));
  }, [activityFeed]);

  // Wire order fills/rejections into bell
  useEffect(() => {
    const prev = prevOrdersRef.current;
    const changed = orders.filter(o => {
      const p = prev.find(x => x.id === o.id);
      return p && p.status !== o.status &&
        (o.status === 'filled' || o.status === 'rejected' || o.status === 'partially_filled');
    });
    if (changed.length > 0) {
      const mapped = changed.map(o => makeNotif('order',
        o.status === 'filled'
          ? `Filled: ${o.side.toUpperCase()} ${o.qty.toLocaleString()} ${o.symbol}`
          : o.status === 'rejected'
          ? `Rejected: ${o.side.toUpperCase()} ${o.qty.toLocaleString()} ${o.symbol}`
          : `Partial fill: ${o.cumQty.toLocaleString()}/${o.qty.toLocaleString()} ${o.symbol}`
      ));
      setNotifs(prev => [...mapped, ...prev].slice(0, 60));
    }
    prevOrdersRef.current = orders;
  }, [orders]);

  // Close notification panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setIsNotifOpen(false);
    };
    if (isNotifOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isNotifOpen]);

  const unreadCount = notifs.filter(n => !n.read).length;
  const openNotifs = () => {
    setIsNotifOpen(o => !o);
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleLogout = () => {
    logout();
    setIsSettingsDropdownOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return '#36c98e';
      case 'connecting': return '#ff8c3a';
      case 'disconnected': return '#ff5c5c';
      default: return '#6b7280';
    }
  };

  const tickerData = useMemo(() =>
    instruments.map(it => {
      const data = vwapMap[it.secId.toUpperCase()] ?? [];
      const latest = data.length > 0 ? data[data.length - 1].vwap : null;
      const first = data.length > 1 ? data[0].vwap : null;
      const delta = latest != null && first != null && first !== 0
        ? ((latest - first) / first) * 100 : null;
      return { secId: it.secId, label: it.label, price: latest, delta };
    }),
    [instruments, vwapMap]
  );

  const netPnl = portfolio.totalUnrealizedPnL + portfolio.totalRealizedPnL;

  return (
    <div className="app-header topbar" style={{
      padding: '0 20px',
      height: '52px',
      borderBottom: '1px solid #2a3540',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'fixed',
      top: 0, left: 0, right: 0,
      zIndex: 200,
    }}>
      {/* Desktop left zone */}
      <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 auto', minWidth: 0 }}>
        <img
          src="/Shabba-Alternative-Logo-01.png"
          alt="Shabba Financial"
          className="header-logo"
          style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
        />
        {/* P2-18: engine-mode chip so the operator can always see SIM vs live at a glance
            (red REAL_DSE / amber HETZNER / slate SIMULATOR). */}
        {(() => {
          const m = engineConfig?.mode ?? 'SIMULATOR';
          const c = m === 'REAL_DSE' ? { fg: '#ff5c5c', bg: 'rgba(255,92,92,0.15)', br: 'rgba(255,92,92,0.4)' }
                  : m === 'HETZNER'  ? { fg: '#ff8c3a', bg: 'rgba(255,140,58,0.15)', br: 'rgba(255,140,58,0.4)' }
                  :                    { fg: '#7e8a96', bg: 'rgba(122,134,147,0.15)', br: 'rgba(122,134,147,0.4)' };
          return (
            <span title={`Engine mode: ${m}`} style={{
              flexShrink: 0, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
              letterSpacing: '0.04em', fontFamily: 'monospace',
              color: c.fg, backgroundColor: c.bg, border: `1px solid ${c.br}`,
            }}>{m}</span>
          );
        })()}
        <div style={{ width: 1, height: 24, backgroundColor: '#2a3540', flexShrink: 0 }} />

        {/* Scrolling ticker */}
        <div
          style={{
            flex: '1 1 0', minWidth: 90, overflow: 'hidden',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)',
            maskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)',
          }}
          onMouseEnter={e => {
            const track = e.currentTarget.querySelector('.ticker-track') as HTMLElement | null;
            if (track) track.style.animationPlayState = 'paused';
          }}
          onMouseLeave={e => {
            const track = e.currentTarget.querySelector('.ticker-track') as HTMLElement | null;
            if (track) track.style.animationPlayState = 'running';
          }}
        >
          <div
            className="ticker-track"
            style={{
              display: 'flex', alignItems: 'center',
              animation: `tickerScroll ${Math.max(tickerData.length * 5, 20)}s linear infinite`,
              width: 'max-content',
            }}
          >
            {[...tickerData, ...tickerData].flatMap((item, i) => {
              const isSelected = item.secId === selectedSymbol;
              return [
                <div
                  key={`item-${i}`}
                  onClick={() => setSelectedSymbol(item.secId)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '0 14px', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: isSelected ? '#93c5fd' : '#7e8a96', letterSpacing: '0.04em' }}>
                    {item.label}
                  </span>
                  {item.price != null ? (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>
                        {item.price.toFixed(2)}
                      </span>
                      {item.delta != null && (
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          color: item.delta >= 0 ? '#36c98e' : '#ff5c5c',
                          backgroundColor: item.delta >= 0 ? 'rgba(54,201,142,0.1)' : 'rgba(255,92,92,0.1)',
                          padding: '1px 5px', borderRadius: 3,
                        }}>
                          {item.delta >= 0 ? '+' : ''}{item.delta.toFixed(2)}%
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 10, color: '#46525e' }}>—</span>
                  )}
                </div>,
                <span key={`sep-${i}`} style={{ color: '#2a3540', fontSize: 12, flexShrink: 0, userSelect: 'none', padding: '0 2px' }}>·</span>,
              ];
            })}
          </div>
        </div>
      </div>

      <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '0 0 auto', marginLeft: 12 }}>
        {/* Net P&L chip */}
        {netPnl !== 0 && (
          <div style={{
            padding: '4px 10px', borderRadius: '6px',
            backgroundColor: netPnl >= 0 ? 'rgba(54,201,142,0.1)' : 'rgba(255,92,92,0.1)',
            border: `1px solid ${netPnl >= 0 ? 'rgba(54,201,142,0.25)' : 'rgba(255,92,92,0.25)'}`,
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <span style={{ fontSize: '11px', color: '#7a8693' }}>P&L</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: netPnl >= 0 ? '#36c98e' : '#ff5c5c', fontVariantNumeric: 'tabular-nums' }}>
              {netPnl >= 0 ? '+' : ''}{(netPnl / 1000).toFixed(1)}K
            </span>
          </div>
        )}

        {/* Connection status dot */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: 7, height: 7, borderRadius: '50%',
            backgroundColor: getStatusColor(connectionStatus),
            boxShadow: connectionStatus === 'connected' ? `0 0 6px ${getStatusColor(connectionStatus)}` : 'none',
          }} />
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#7a8693', letterSpacing: '0.04em' }}>
            {connectionStatus === 'connected' ? 'LIVE' : connectionStatus === 'connecting' ? 'SYNC' : 'OFFLINE'}
          </span>
        </div>

        <div style={{ width: 1, height: 20, backgroundColor: '#2a3540', flexShrink: 0 }} />

        {/* Auto-trade toggle */}
        <button
          className="start-trading-button"
          onClick={() => setIsAutoTrading(!isAutoTrading)}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            padding: '5px 12px',
            backgroundColor: isAutoTrading ? 'rgba(255,92,92,0.12)' : 'rgba(54,201,142,0.1)',
            color: isAutoTrading ? '#f87171' : '#34d399',
            border: `1px solid ${isAutoTrading ? 'rgba(255,92,92,0.3)' : 'rgba(54,201,142,0.25)'}`,
            borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700',
            letterSpacing: '0.04em',
          }}
        >
          {isAutoTrading ? <Pause size={12} /> : <Play size={12} />}
          <span className="start-trading-text">{isAutoTrading ? 'STOP' : 'AUTO'}</span>
        </button>

        {/* Notification bell */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <div onClick={openNotifs} style={{ position: 'relative', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
            <Bell size={15} color={unreadCount > 0 ? '#ff8c3a' : '#7a8693'} />
            {unreadCount > 0 && (
              <div style={{
                position: 'absolute', top: 0, right: 0,
                width: 14, height: 14, borderRadius: '50%',
                backgroundColor: '#ff5c5c', color: 'white',
                fontSize: '9px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </div>
          {isNotifOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              backgroundColor: '#10151b', border: '1px solid #2a3540',
              borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
              width: 300, zIndex: 1000,
            }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a3540', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#7e8a96', letterSpacing: '0.06em' }}>NOTIFICATIONS</span>
                {notifs.length > 0 && (
                  <button onClick={() => setNotifs([])} style={{ background: 'transparent', border: 'none', color: '#46525e', fontSize: 11, cursor: 'pointer' }}>
                    Clear all
                  </button>
                )}
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {notifs.length === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: '#46525e', fontSize: 12 }}>No notifications</div>
                ) : notifs.map(n => {
                  const dotColor = n.type === 'connection'
                    ? (n.message.includes('established') ? '#36c98e' : n.message.includes('lost') ? '#ff5c5c' : '#ff8c3a')
                    : n.type === 'engine' ? '#36c98e'
                    : n.type === 'order' ? (n.message.startsWith('Rejected') ? '#ff5c5c' : n.message.startsWith('Partial') ? '#ff8c3a' : '#36c98e')
                    : '#4da3ff';
                  return (
                    <div key={n.id} style={{ padding: '10px 14px', borderBottom: '1px solid #1a2840', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', marginTop: 4, flexShrink: 0, backgroundColor: dotColor }} />
                      <div>
                        <div style={{ fontSize: 12, color: '#cbd5e1' }}>{n.message}</div>
                        <div style={{ fontSize: 10, color: '#46525e', marginTop: 2 }}>{new Date(n.time).toLocaleTimeString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div ref={settingsDropdownRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
            style={{
              width: 30, height: 30, borderRadius: '50%',
              backgroundColor: '#36c98e',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: '700', fontSize: '13px',
              cursor: 'pointer', userSelect: 'none', flexShrink: 0,
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          {isSettingsDropdownOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: '8px',
              backgroundColor: '#10151b', border: '1px solid #2a3540',
              borderRadius: '8px', padding: '8px 0', minWidth: '200px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)', zIndex: 1000,
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a3540', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#36c98e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px' }}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#d7dde4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.name || 'User'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#7e8a96', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email || ''}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { onNavigate('settings'); setIsSettingsDropdownOpen(false); }}
                style={{ width: '100%', padding: '10px 16px', backgroundColor: 'transparent', border: 'none', color: '#d7dde4', fontSize: '13px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'background-color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2a3540')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Settings size={14} />
                <span>Settings</span>
              </button>
              <button
                onClick={handleLogout}
                style={{ width: '100%', padding: '10px 16px', backgroundColor: 'transparent', border: 'none', borderTop: '1px solid #2a3540', color: '#ff5c5c', fontSize: '13px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'background-color 0.2s', marginTop: '4px' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#7f1d1d')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <LogOut size={14} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout. The hamburger that opened the off-canvas sidebar is
          gone with the sidebar itself -- the bottom nav rail is reachable at
          every viewport width, so there is nothing left for it to toggle. */}
      <img src="/Shabba-Alternative-Logo-01.png" alt="Shabba Financial" className="header-logo-mobile" style={{ width: 'auto', objectFit: 'contain', display: 'none' }} />

      <div className="header-wifi-mobile" style={{ display: 'none', alignItems: 'center' }}>
        {connectionStatus === 'connected' ? <Wifi size={18} color={getStatusColor(connectionStatus)} /> : <WifiOff size={18} color={getStatusColor(connectionStatus)} />}
      </div>

      <select
        className="header-select-mobile"
        value={selectedSymbol}
        onChange={e => setSelectedSymbol(e.target.value)}
        style={{ display: 'none', backgroundColor: '#2a3540', color: '#d7dde4', border: '1px solid #46525e', borderRadius: '6px' }}
      >
        {INSTRUMENTS.map(it => <option key={it.secId} value={it.secId}>{it.label}</option>)}
      </select>

      <button
        className="start-trading-button-mobile"
        onClick={() => setIsAutoTrading(!isAutoTrading)}
        style={{ display: 'none', alignItems: 'center', justifyContent: 'center', padding: '3px 5px', backgroundColor: isAutoTrading ? '#ff5c5c' : '#36c98e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
      >
        {isAutoTrading ? <Pause size={12} /> : <Play size={12} />}
      </button>

      <div ref={settingsDropdownRef} style={{ position: 'relative', display: 'none' }} className="header-settings-mobile">
        <Settings size={12} onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)} style={{ cursor: 'pointer', color: '#7e8a96', flexShrink: 0 }} />
        {isSettingsDropdownOpen && (
          <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', backgroundColor: '#10151b', border: '1px solid #2a3540', borderRadius: '8px', padding: '8px 0', minWidth: '180px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)', zIndex: 1000 }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #2a3540', fontSize: '12px', color: '#d7dde4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || 'User'}
            </div>
            <button
              onClick={handleLogout}
              style={{ width: '100%', padding: '8px 12px', backgroundColor: 'transparent', border: 'none', color: '#ff5c5c', fontSize: '12px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background-color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#7f1d1d')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <LogOut size={12} />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
